import { useState, useEffect, useMemo, useRef } from 'react';
import { getDataGraph, valueFormat } from 'components/3catinfo/Eleccions/Grafiques/GraficEleccions/dataParserEleccions';
import { useEleccions } from 'hooks/3catinfo/useEleccions';
import { useViewport } from 'hooks/useViewport';
import { useModal } from 'hooks/3catinfo/useModal';
import { useEmitter } from 'hooks/useEmitter';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import styles from './llegendaEleccions.module.scss';
import Icona from 'components/3catinfo/ui/Icona';

export const LlegendaEleccionsDades = (props) => {
    const {
        emitterId,
        urlDades,
        urlDadesComparador,
        intervalRefresh = 0,
        tipusLlegenda,
        estilLlegenda,
        mostrarPartitsSenseDiputats,
        mostrarVeureTots,
        mostrarComparativa,
        fase,
        limitItems,
        limitValueCondition = 1,
        limitValueField,
        limitValueStrict = false,
        tipusEleccions = null,
        isModal,
        carrec,
        dataTestId,
        numRowsSEO
    } = props;
    const { dataActual, dataComparador, isDataLoaded, getDades } = useEleccions();

    useEffect(() => {
        getDades({ urlDades, urlDadesComparador, intervalRefresh });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlDades]);

    if (!isDataLoaded) return <></>;

    return (
        <LlegendaEleccions
            fase={fase}
            dataActual={dataActual?.datos?.geo}
            dataComparador={dataComparador?.datos?.geo}
            emitterId={emitterId}
            tipus={tipusLlegenda}
            estil={estilLlegenda}
            integratAlGrafic={false}
            mostrarPartitsSenseDiputats={mostrarPartitsSenseDiputats}
            mostrarVeureTots={mostrarVeureTots}
            mostrarComparativa={mostrarComparativa}
            limitItems={limitItems}
            limitValueCondition={limitValueCondition}
            limitValueField={limitValueField}
            limitValueStrict={limitValueStrict}
            tipusEleccions={tipusEleccions}
            isModal={isModal}
            dataTestId={dataTestId}
            carrec={carrec}
            numRowsSEO={numRowsSEO}
        />
    );
};

const getColumnsLlegenda = (tipusCarrec) => ({
    diputatsIVots: [
        { label: 'Partit', key: 'id' },
        { label: `${tipusCarrec}`, key: 'numitems' },
        { label: '% Vots', key: 'perc' },
        {
            label: 'Vots',
            key: 'vots',
            filter: ({ isModal, fase }) => isModal && fase !== 'sondeig'
        },
        { label: `${tipusCarrec}`, key: 'numitems', isComparador: true },
        { label: '% Vots', key: 'perc', isComparador: true },
        {
            label: 'Vots',
            key: 'vots',
            isComparador: true,
            filter: ({ isModal, showSecondaryGraph }) => isModal && showSecondaryGraph
        }
    ],
    diputats: [
        { label: 'Partit', key: 'id' },
        { label: `${tipusCarrec}`, key: 'numitems' },
        { label: `${tipusCarrec}`, key: 'numitems', isComparador: true }
    ],
    vots: [
        { label: 'Partit', key: 'id' },
        { label: '% Vots', key: 'perc' },
        {
            label: 'Vots',
            key: 'vots',
            filter: ({ isModal, showSecondaryGraph }) => !showSecondaryGraph || (isModal && showSecondaryGraph)
        },
        { label: '% Vots', key: 'perc', isComparador: true },
        {
            label: 'Vots',
            key: 'vots',
            isComparador: true,
            filter: ({ isModal, showSecondaryGraph }) => isModal && showSecondaryGraph
        }
    ],
    votsPercentatge: [
        { label: 'Partit', key: 'id' },
        { label: '% Vots', key: 'perc' },
        { label: '% Vots', key: 'perc', isComparador: true }
    ]
});

const _getDifference = (a, b, decimals = 0, showZeroDecimals = false, showInterval = false) => {
    const styleDiferenciaNoTrobada = clsx({
        [styles.diferencia]: true,
        [styles.noTrobat]: true
    });
    if (b === undefined || b === null || b === '') return <span className={styleDiferenciaNoTrobada} />;
    let parsedA = a;
    if (typeof a === 'string' && a.includes('-')) {
        const [left, right] = a.split('-').map((v) => Number(v.trim()));
        if (!isNaN(left) && !isNaN(right)) {
            parsedA = (left + right) / 2;
        }
    }

    const numA = Number(parsedA);
    const numB = Number(b);
    if (isNaN(numA) || isNaN(numB)) {
        return <span className={styleDiferenciaNoTrobada} />;
    }
    const diff = numA - numB;
    let absDiff = Math.abs(diff);
    const rounded = absDiff.toFixed(decimals);
    const valueToShow = valueFormat(null, rounded, decimals, false, showZeroDecimals);
    const styleDiferencia = clsx({
        [styles.diferencia]: true,
        [styles.puja]: diff > 0,
        [styles.baixa]: diff < 0,
        [styles.igual]: diff === 0
    });

    const getValueDiff = (value) => {
        const numValue = Number(value.trim().replaceAll('.', '').replaceAll(',', '.'));
        if (!showInterval || Number.isInteger(numValue)) return value;
        const min = Math.floor(numValue);
        const max = Math.ceil(numValue);
        return `${min}-${max}`;
    };

    const valueDiff = diff === 0 ? '' : getValueDiff(valueToShow);
    return <span className={styleDiferencia}>{valueDiff}</span>;
};

const ItemValorLlegenda = (props) => {
    const { camp, isComparador, item, itemComparador, mostrarComparativa, showSecondaryGraph, dataPartitsComparador, fase } =
        props;

    const value = isComparador ? itemComparador?.[camp] : item?.[camp];
    const isFaseSeoComparadorDisabled = fase === 'seo' && !showSecondaryGraph;

    const styleCamp = clsx({
        [styles.ambComparativa]:
            mostrarComparativa && !showSecondaryGraph && !isFaseSeoComparadorDisabled && !__isEmpty(dataPartitsComparador)
    });

    const Comparativa = (props) => {
        const { decimals } = props;
        if (isFaseSeoComparadorDisabled) return <></>;
        if (!mostrarComparativa || isComparador || showSecondaryGraph || __isEmpty(dataPartitsComparador)) return <></>;
        const showInterval = fase === 'sondeig' && camp === 'numitems';
        return _getDifference(item[camp], itemComparador?.[camp], decimals || 1, false, showInterval);
    };

    switch (camp) {
        case 'id':
            return isFaseSeoComparadorDisabled ? (
                <p>-</p>
            ) : (
                <p>
                    <span
                        className={styles.boletaPartit}
                        style={{ backgroundColor: 'rgb(' + item.rcolor + ', ' + item.gcolor + ', ' + item.bcolor + ')' }}
                    />{' '}
                    {value}
                </p>
            );
        case 'perc':
            return (
                <p className={styleCamp}>
                    <span>{valueFormat(fase, value, 2, true)} </span>
                    <Comparativa decimals={2} />
                </p>
            );
        case 'vots':
            return (
                <p className={styleCamp}>
                    <span>{valueFormat(fase, value)}</span>
                    <Comparativa />
                </p>
            );
        default:
            return (
                <p className={styleCamp}>
                    <span>{valueFormat(fase, value)}</span>
                    <Comparativa />
                </p>
            );
    }
};

const HeaderRow1 = (props) => {
    const { titolActual, titolComparador, showSecondaryGraph, visibleColumns } = props;

    if (showSecondaryGraph) {
        return (
            <tr>
                <th rowSpan={2} className={styles.columnaFix}>
                    {visibleColumns.find((col) => col.key === 'id').label}
                </th>
                <th colSpan={visibleColumns.filter((col) => !col.isComparador).length - 1}>{titolActual}</th>
                <th colSpan={visibleColumns.filter((col) => col.isComparador).length}>{titolComparador}</th>
            </tr>
        );
    } else {
        return (
            <tr>
                {visibleColumns?.map((col, index) => (
                    <th key={`llegenda-header-column-${col.key}`} className={index === 0 ? styles.columnaFix : ''}>
                        {col.label}
                    </th>
                ))}
            </tr>
        );
    }
};

const HeaderRow2 = (props) => {
    const { visibleColumns, showSecondaryGraph } = props;
    if (!showSecondaryGraph) return <></>;

    return (
        <tr>
            {visibleColumns?.map((col, index) => {
                if (col.key !== 'id') {
                    return (
                        <th className={styles.headerRow2} key={`llegenda-header-column-${col.key}-${index}`}>
                            {col.label}
                        </th>
                    );
                }
            })}
        </tr>
    );
};

const ModalResultats = (props) => {
    const {
        removeModal,
        fase,
        dataActual,
        dataComparador,
        emitterId,
        tipus,
        estil,
        showSecondaryGraph,
        tipusEleccions = null,
        carrec
    } = props;
    return (
        <div className={styles.modalMask}>
            <div className={styles.modalWrapper}>
                <div className={styles.modalContent}>
                    <div className={styles.controls}>
                        <div className={styles.controlsInner}>
                            <button onClick={removeModal}>
                                <Icona className={styles.iconaTancar} icona='tanca' alt='tanca' mida='24' isBlanc={false} />
                            </button>
                        </div>
                    </div>
                    <div className={styles.modalContainer}>
                        <div className={styles.content}>
                            <h3>Tots els resultats</h3>
                            <div className={styles.scrollable}>
                                <LlegendaEleccions
                                    fase={fase}
                                    dataActual={dataActual}
                                    dataComparador={dataComparador}
                                    emitterId={emitterId}
                                    tipus={tipus}
                                    estil={estil}
                                    integratAlGrafic={false}
                                    mostrarSegonGrafic={showSecondaryGraph}
                                    mostrarPartitsSenseDiputats={true}
                                    isModal={true}
                                    limitValueCondition={null}
                                    tipusEleccions={tipusEleccions}
                                    carrec={carrec}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const buildSeoPlaceholderRows = (targetRows) =>
    Array.from({ length: targetRows }, (_, index) => ({
        id: index === 0 ? '-' : `- ${index + 1}`,
        rcolor: 0,
        gcolor: 0,
        bcolor: 0,
        numitems: '-',
        perc: '-',
        vots: '-'
    }));

const getRowsFilter = ({
    dataPartitsActual,
    dataPartitsComparador,
    fase,
    limitItems,
    showSecondaryGraph,
    mostrarPartitsSenseDiputats,
    integratAlGrafic,
    estil,
    limitValueCondition,
    limitValueField,
    limitValueStrict,
    hideZeroVoteRows,
    numRowsSEO,
    tipus
}) => {
    const seoRowCount = Number(numRowsSEO);
    const targetRows = Number.isInteger(seoRowCount) && seoRowCount > 0 ? seoRowCount : Number(limitItems);
    const placeholderRows =
        fase === 'seo' &&
        estil === 'slider' &&
        integratAlGrafic &&
        !showSecondaryGraph &&
        Number.isInteger(targetRows) &&
        targetRows > 0
            ? buildSeoPlaceholderRows(targetRows)
            : null;

    let rowsBase;
    if (placeholderRows) {
        rowsBase = placeholderRows;
    } else if (fase === 'seo') {
        rowsBase = dataPartitsComparador
            ?.filter((item) => mostrarPartitsSenseDiputats || item.mnumitems > 0)
            .map((item) => ({
                ...item,
                numitems: '-',
                perc: '-',
                vots: '-'
            }));
        if (!showSecondaryGraph && integratAlGrafic) {
            const safeRowsBase = rowsBase ?? [];
            const rowsBaseLen = safeRowsBase.length;
            if (rowsBaseLen > 0) {
                rowsBase = Array.from({ length: 8 }, (_, i) => safeRowsBase[i % rowsBaseLen]);
            }
        } else if (!showSecondaryGraph && numRowsSEO) {
            const safeRowsBase = rowsBase ?? [{ id: '' }];
            const targetRows = Number(numRowsSEO);
            if (safeRowsBase.length < targetRows) {
                rowsBase = Array.from({ length: targetRows }, (_, i) => safeRowsBase[i % safeRowsBase.length]);
            } else {
                rowsBase = safeRowsBase.slice(0, targetRows);
            }
        }
    } else {
        if (showSecondaryGraph) {
            const comparadorMap = new Map((dataPartitsComparador || []).map((item) => [item.id, item]));
            rowsBase = [];
            for (const actual of dataPartitsActual || []) {
                const comparador = comparadorMap.get(actual.id);
                const actualValue = actual?.mnumitems || 0;
                const comparadorValue = comparador?.mnumitems || 0;
                if (!mostrarPartitsSenseDiputats && actualValue === 0 && comparadorValue === 0) {
                    continue;
                }
                rowsBase.push(actual);
                comparadorMap.delete(actual.id);
            }
            if (!hideZeroVoteRows) {
                for (const comparador of comparadorMap.values()) {
                    const comparadorValue = comparador?.mnumitems || 0;
                    if (!mostrarPartitsSenseDiputats && comparadorValue === 0) {
                        continue;
                    }
                    rowsBase.push({
                        ...comparador,
                        numitems: '-',
                        perc: '-',
                        vots: '-'
                    });
                }
            }
        } else {
            const isPercentageFilter = limitValueField === 'mperc' || limitValueField === 'perc';
            rowsBase = dataPartitsActual?.filter((item) => {
                if (mostrarPartitsSenseDiputats || isPercentageFilter) {
                    return true;
                }
                return item.mnumitems > 0;
            });
        }
    }

    const hasSeoRowLimitActive = numRowsSEO !== null && numRowsSEO !== undefined && numRowsSEO !== '' && !showSecondaryGraph;
    if (limitValueCondition !== null && !hasSeoRowLimitActive && !placeholderRows) {
        const filterField = limitValueField || (hideZeroVoteRows ? 'mperc' : null);
        if (filterField) {
            const parseNumeric = (raw) => {
                if (typeof raw === 'number') return raw;
                if (typeof raw === 'string') {
                    const normalized = raw.trim().replace(',', '.');
                    const parsed = Number(normalized);
                    return Number.isFinite(parsed) ? parsed : NaN;
                }
                const parsed = Number(raw ?? NaN);
                return Number.isFinite(parsed) ? parsed : NaN;
            };

            rowsBase = rowsBase?.filter((item) => {
                const rawValue =
                    filterField === 'mperc'
                        ? (item?.mperc ?? item?.perc)
                        : filterField === 'perc'
                          ? (item?.perc ?? item?.mperc)
                          : item?.[filterField];
                let value = parseNumeric(rawValue);

                if (!Number.isFinite(value) && (filterField === 'perc' || filterField === 'mperc')) {
                    const fallbackRaw = filterField === 'perc' ? item?.mperc : item?.perc;
                    value = parseNumeric(fallbackRaw);
                }

                if (!Number.isFinite(value)) return false;
                return limitValueStrict ? value > limitValueCondition : value >= limitValueCondition;
            });
        }
    }

    if (limitItems && limitItems < rowsBase?.length) {
        rowsBase = rowsBase.slice(0, limitItems);
    }

    if (hideZeroVoteRows && !hasSeoRowLimitActive && !placeholderRows) {
        rowsBase = rowsBase?.filter((item) => {
            const zeroValueField =
                tipus === 'diputatsIVots' || tipus === 'diputats' ? (item?.mnumitems ?? item?.numitems) : item?.vots;
            return Number(zeroValueField ?? 0) > 0;
        });
    }

    return rowsBase;
};

const _showColumnDefault = ({ showSecondaryGraph, col }) => {
    return showSecondaryGraph ? true : !col.isComparador;
};

const hasNonZeroVotes = (items = []) => {
    return items?.some((item) => Number(item?.vots ?? 0) > 0);
};

const LlegendaEleccions = (props) => {
    const {
        dataActual,
        dataComparador,
        emitterId,
        tipus = 'diputatsIVots',
        mostrarPartitsSenseDiputats = false,
        estil,
        className,
        mostrarComparativa = true,
        mostrarVeureTots = false,
        mostrarSegonGrafic = false,
        integratAlGrafic = true,
        fase,
        numColumnsRow,
        limitItems = 0,
        limitValueCondition = 1,
        limitValueField,
        limitValueStrict = false,
        tipusEleccions = null,
        dataTestId,
        carrec = 'Regidors',
        numRowsSEO
    } = props;
    const tipusCarrec = tipusEleccions === 'municipals' ? carrec : 'Diputats';
    const { isOnlyMobile } = useViewport();
    const { setModal, removeModal } = useModal();
    const { onEmitter } = useEmitter();
    const tableWrapperRef = useRef(null);
    const tableRef = useRef(null);
    const [showSecondaryGraph, setShowSecondaryGraph] = useState(mostrarSegonGrafic || false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const dataPartitsActual = getDataGraph('partitsByConvocatoria', dataActual);
    const dataPartitsComparador = getDataGraph('partitsByConvocatoria', dataComparador);
    const isFaseSeoComparadorDisabled = fase === 'seo' && !showSecondaryGraph;

    const visibleColumns = useMemo(() => {
        const columnsLlegenda = getColumnsLlegenda(tipusCarrec);
        return columnsLlegenda[tipus]
            ?.map((col) => ({
                ...col,
                visible: col.filter
                    ? col.filter({ ...props, showSecondaryGraph })
                    : _showColumnDefault({ showSecondaryGraph, col })
            }))
            .filter((col) => {
                if (!col.visible) return false;
                if (fase === 'seo' || col.key !== 'vots') return true;
                const sourceItems = col.isComparador ? dataPartitsComparador : dataPartitsActual;
                return hasNonZeroVotes(sourceItems);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dataPartitsActual,
        dataPartitsComparador,
        showSecondaryGraph,
        fase,
        mostrarComparativa,
        mostrarPartitsSenseDiputats,
        limitItems,
        limitValueCondition,
        tipus,
        tipusCarrec
    ]);

    const hasVisibleVoteColumn = visibleColumns?.some((col) => col.key === 'vots');

    const rowsBase = getRowsFilter({
        dataPartitsActual,
        dataPartitsComparador,
        fase,
        limitItems,
        showSecondaryGraph,
        mostrarPartitsSenseDiputats,
        integratAlGrafic,
        estil,
        limitValueCondition,
        limitValueField,
        limitValueStrict,
        hideZeroVoteRows: hasVisibleVoteColumn,
        numRowsSEO,
        tipus
    });

    onEmitter(emitterId, (value) => {
        setShowSecondaryGraph(value);
    });

    useEffect(() => {
        const wrapper = tableWrapperRef.current;
        const table = tableRef.current;
        if (!wrapper || !table) return;

        const checkOverflow = () => {
            const wrapperWidth = wrapper.clientWidth - 24;
            const tableWidth = table.scrollWidth;
            setHasOverflow(tableWidth >= wrapperWidth);
        };

        checkOverflow();
    }, [visibleColumns, rowsBase, showSecondaryGraph]);

    const handleOnClickShowAll = () => {
        setModal(<ModalResultats {...props} removeModal={removeModal} showSecondaryGraph={showSecondaryGraph} />, {
            className: styles.Llegenda,
            closeOnClickOutside: true
        });
    };

    const styleLlegenda = clsx(
        styles.llegenda,
        !integratAlGrafic && styles.ambBorder,
        estil && styles[estil],
        estil === 'slider' && fase === 'seo' && tipus === 'votsPercentatge' && styles.sliderSeoVots,
        estil === 'slider' && fase === 'seo' && tipus === 'diputats' && styles.sliderSeoDiputats,
        numColumnsRow > 0 && styles['col' + numColumnsRow],
        !__isEmpty(className) && classNames
    );

    const styleLlegendaWrapper = clsx(
        styles.llegendaWrapper,
        !integratAlGrafic && fase === 'seo' && styles.llegendaSeo,
        isOnlyMobile && hasOverflow && styles.ambScroll
    );

    const stylesVeureTots = clsx(styles.footerContent, isFaseSeoComparadorDisabled && styles.disabled);

    const numColumns = visibleColumns?.length;

    return (
        <div className={styleLlegendaWrapper} ref={tableWrapperRef} {...(dataTestId && { 'data-testid': dataTestId })}>
            <div className={styles.tableScroll}>
                <table className={styleLlegenda} ref={tableRef}>
                    <colgroup>
                        <col className={styles.columnaPartit} />
                        <col span='4' style={{ width: `${100 / (numColumns - 1)}%` }} />
                    </colgroup>
                    <thead className={styles.head}>
                        <HeaderRow1
                            titolActual={fase === 'sondeig' ? 'Sondeig' : dataActual?.year}
                            titolComparador={dataComparador?.year}
                            showSecondaryGraph={showSecondaryGraph}
                            visibleColumns={visibleColumns}
                        />
                        <HeaderRow2 showSecondaryGraph={showSecondaryGraph} visibleColumns={visibleColumns} />
                    </thead>
                    <tbody className={styles.body}>
                        {rowsBase?.map((item, index) => {
                            const itemComparador = dataPartitsComparador?.find((itemC) => itemC.id === item?.id);
                            return (
                                <tr key={`llegenda-data-row-${index}-${item?.id}`}>
                                    {visibleColumns?.map((col, col_index) => {
                                        const styleColumnaDada = clsx({
                                            [styles.columnaDada]: true,
                                            [styles.partit]: col.key === 'id',
                                            [styles.columnaFix]: col_index === 0
                                        });
                                        return (
                                            <td
                                                key={`llegenda-data-column-${index}-${col_index}-${item?.id}-${col.key}`}
                                                className={styleColumnaDada}
                                            >
                                                <ItemValorLlegenda
                                                    fase={fase}
                                                    camp={col.key}
                                                    isComparador={col.isComparador}
                                                    item={item}
                                                    itemComparador={itemComparador}
                                                    mostrarComparativa={mostrarComparativa}
                                                    showSecondaryGraph={showSecondaryGraph}
                                                    dataPartitsComparador={dataPartitsComparador}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                    {mostrarVeureTots && (
                        <tfoot>
                            <tr>
                                <th className={styles.footer} colSpan={numColumns}>
                                    <div className={stylesVeureTots} onClick={handleOnClickShowAll} tabIndex={0} role='button'>
                                        <span className={styles.text}>Tots els resultats</span>
                                        <Icona icona='mes' className={styles.icona} inline={true} alt='' />
                                    </div>
                                </th>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

export default LlegendaEleccions;
